import React, { useState, useCallback } from "react";
import {
  ContextualSaveBar,
  Banner,
  BlockStack,
  Box,
  Card,
  Layout,
  Loading,
  Page,
  SkeletonDisplayText,
  Text,
} from "@medentic/polaris-react";
import ReactMarkdown from "react-markdown";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { EntityType } from "@shared/types/commonTypes";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useParams } from "react-router-dom";
import { SettingsPageWrapper } from "@shared/ui/Settings";
import { IbanField } from "@shared/ui";
import { EventOrganizerPayoutDTO } from "@shared/types/billingTypes";

const SettingsPayouts: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { GET, PUT } = useApiRequest();
  const params = useParams<{ eventOrganizerId: string }>();
  const companyId = params.eventOrganizerId ?? "";
  const [draftBankIban, setDraftBankIban] = useState<string | null>(null);
  const [isIbanValid, setIsIbanValid] = useState<boolean>(false);

  // Fetch payout data
  const {
    data: payoutData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["payout-settings", companyId],
    queryFn: async () => {
      try {
        const response = await GET<EventOrganizerPayoutDTO>(
          `/api/healthcare-companies/${companyId}/billing/payout`,
          false,
          false,
          true // suppress toast errors for 404
        );
        return response;
      } catch {
        // Handle 404 or other errors - treat as no payout contract
        return null;
      }
    },
    enabled: !!companyId,
    retry: false,
  });

  const hasPayoutContract = Boolean(payoutData) && !isError;
  const originalBankIban = payoutData?.bankIban || "";
  const bankIban = draftBankIban ?? originalBankIban;
  const hasUnsavedChanges = draftBankIban !== null && bankIban !== originalBankIban;

  const handleIbanChange = (value: string) => {
    setDraftBankIban(value);
  };

  const handleValidityChange = useCallback((isValid: boolean) => {
    setIsIbanValid(isValid);
  }, []);

  const updatePayout = useMutation({
    mutationFn: async (updatedData: { bankIban: string }) => {
      return await PUT(
        `/api/healthcare-companies/${companyId}/billing/payout`,
        updatedData
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payout-settings", companyId] });
      setDraftBankIban(null);
    },
    onError: () => {
      // Error toast is handled by useApiRequest
    },
  });

  const handleDiscard = () => {
    setDraftBankIban(null);
    setIsIbanValid(Boolean(originalBankIban));
  };

  const handleSave = () => {
    if (hasUnsavedChanges && isIbanValid) {
      updatePayout.mutate({ bankIban });
    }
  };

  const canSave = hasUnsavedChanges && isIbanValid;

  const renderSkeleton = () => (
    <BlockStack gap="200">
      <SkeletonDisplayText size="small" />
      <SkeletonDisplayText size="small" />
    </BlockStack>
  );

  const formatPaymentFee = (fee: number | undefined) => {
    if (fee === undefined) return "—";
    // Convert decimal to percentage (0.03 -> 3%)
    return `${(fee * 100).toFixed(0)}%`;
  };

  const formatInvoiceNumber = (num: number | undefined) => {
    if (num === undefined) return "—";
    return String(num);
  };

  return (
    <SettingsPageWrapper entityType={EntityType.EVENT} entityId={companyId}>
      <Page title={t("payoutSettings.title")} narrowWidth>
        {isLoading && <Loading />}
        {hasUnsavedChanges && (
          <ContextualSaveBar
            backgroundColor="#2B0069"
            saveAction={{
              onAction: handleSave,
              loading: updatePayout.isPending,
              disabled: !canSave,
            }}
            discardAction={{
              onAction: handleDiscard,
            }}
          />
        )}

        <Layout>
          {/* Inactive Banner */}
          {!hasPayoutContract && !isLoading && (
            <Layout.Section>
              <Banner
                title={t("payoutSettings.inactiveBanner.title")}
                tone="info"
              >
                <Text as="p">{t("payoutSettings.inactiveBanner.description")}</Text>
              </Banner>
            </Layout.Section>
          )}

          {/* Online Payment Fee Card */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h1" variant="headingMd">
                  {t("payoutSettings.onlinePaymentFee.title")}
                </Text>

                {isLoading ? (
                  renderSkeleton()
                ) : !hasPayoutContract ? (
                  <BlockStack gap="200">
                    <SkeletonDisplayText size="small" maxWidth="10ch" />
                    <Text as="p" tone="subdued">
                      {t("payoutSettings.onlinePaymentFee.placeholder")}
                    </Text>
                  </BlockStack>
                ) : (
                  <Text as="p" tone="subdued">
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <>{children}</>,
                        strong: ({ children }) => <Text as="span" fontWeight="bold">{children}</Text>,
                      }}
                    >
                      {t("payoutSettings.onlinePaymentFee.description", {
                        fee: `**${formatPaymentFee(payoutData?.paymentFee)}**`,
                      })}
                    </ReactMarkdown>
                  </Text>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Receivable Bank Account Card */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <BlockStack gap="200">
                  <Text as="h1" variant="headingMd">
                    {t("payoutSettings.bankAccount.title")}
                  </Text>
                  <Text as="p" tone="subdued">
                    {t("payoutSettings.bankAccount.description")}
                  </Text>
                </BlockStack>

                {isLoading ? (
                  renderSkeleton()
                ) : !hasPayoutContract ? (
                  <BlockStack gap="100">
                    <Text as="p" variant="bodySm" tone="subdued">
                      {t("payoutSettings.bankAccount.ibanLabel")}
                    </Text>
                    <SkeletonDisplayText size="small" />
                  </BlockStack>
                ) : (
                  <IbanField
                    label={t("payoutSettings.bankAccount.ibanLabel")}
                    value={bankIban}
                    onChange={handleIbanChange}
                    onValidityChange={handleValidityChange}
                    required
                  />
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Invoice Details Card */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <BlockStack gap="200">
                  <Text as="h1" variant="headingMd">
                    {t("payoutSettings.invoiceDetails.title")}
                  </Text>
                  {isLoading || !hasPayoutContract ? (
                    <BlockStack gap="100">
                      <Text as="p" tone="subdued">
                        {t("payoutSettings.invoiceDetails.placeholder")}
                      </Text>
                      <SkeletonDisplayText size="small" />
                    </BlockStack>
                  ) : (
                    <Text as="p" tone="subdued">
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <>{children}</>,
                          strong: ({ children }) => <Text as="span" fontWeight="bold">{children}</Text>,
                        }}
                      >
                        {t("payoutSettings.invoiceDetails.description", {
                          startRange: `**${formatInvoiceNumber(payoutData?.invoiceStartRange)}**`,
                          endRange: `**${formatInvoiceNumber(payoutData?.invoiceEndRange)}**`,
                          contractReference: `**${payoutData?.contractReference || "—"}**`,
                        })}
                      </ReactMarkdown>
                    </Text>
                  )}
                </BlockStack>

                <BlockStack gap="100">
                  <Text as="p" tone="subdued">
                    {t("payoutSettings.invoiceDetails.nextInvoiceNumber")}
                  </Text>
                  {isLoading || !hasPayoutContract ? (
                    <SkeletonDisplayText size="small" maxWidth="15ch" />
                  ) : (
                    <Text as="p" variant="headingMd">
                      {formatInvoiceNumber(payoutData?.invoiceNumberSequence)}
                    </Text>
                  )}
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
        <Box paddingBlock="200" />
      </Page>
    </SettingsPageWrapper>
  );
};

export default SettingsPayouts;
